import { Button } from '@mantine/core';
import { CalendarClock, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';

import PatientDuplicationPopup from '@/components/AppointmentScheduler/PatientDuplicationPopup.tsx';
import { RescheduleNotificationModal } from '@/components/AppointmentScheduler/reschedule-notification-modal.tsx';

import { useSeriesFooter } from '@/hooks/use-series-footer.ts';

import { CollectionStep } from '@/models/appointment/Appointment.ts';

interface Props {
    onClose?: () => void;
}

export const SeriesFooterButtons = ({ onClose }: Props) => {
    const {
        collectionStep,
        isBooking,
        isFetchingSuggestion,
        isAutoSuggestionMode,
        canOfferAutoSuggestion,
        isEditing,
        hasSuggestion,
        hasPreviousSuggestion,
        cannotProceed,
        cannotBook,
        showDuplicationPopup,
        setShowDuplicationPopup,
        showRescheduleModal,
        setShowRescheduleModal,
        handleSeriesNext,
        handleAutoSuggestion,
        handleNextSuggestion,
        handlePreviousSuggestion,
        handleSeriesBack,
        handleSeriesConfirm,
        handleCalendarPlacement,
        handleConfirmDuplication,
        updateCollectionWithGuard,
    } = useSeriesFooter(onClose);

    if (collectionStep === CollectionStep.LIST) {
        return (
            <>
                <div className="flex w-full justify-between">
                    <Button
                        variant="subtle"
                        leftSection={<ChevronLeft className="size-4" />}
                        onClick={handleSeriesBack}
                        disabled={isBooking || isFetchingSuggestion}
                    >
                        Zurück
                    </Button>
                    <div className="flex gap-2">
                        {isAutoSuggestionMode && hasPreviousSuggestion && (
                            <Button
                                variant="outline"
                                leftSection={<ChevronLeft className="size-4" />}
                                onClick={handlePreviousSuggestion}
                                disabled={isBooking || isFetchingSuggestion}
                            >
                                Vorheriger Vorschlag
                            </Button>
                        )}
                        {isAutoSuggestionMode && hasSuggestion && (
                            <Button
                                variant="outline"
                                rightSection={<ChevronRight className="size-4" />}
                                onClick={handleNextSuggestion}
                                disabled={isBooking || isFetchingSuggestion}
                                loading={isFetchingSuggestion}
                            >
                                Nächster Vorschlag
                            </Button>
                        )}
                        {canOfferAutoSuggestion && (
                            <Button
                                variant="outline"
                                leftSection={<CalendarClock className="size-4" />}
                                onClick={handleAutoSuggestion}
                                disabled={isBooking}
                                loading={isBooking}
                            >
                                Auto-Vorschlag
                            </Button>
                        )}
                        {!isAutoSuggestionMode && (
                            <Button
                                variant="outline"
                                leftSection={<MapPin className="size-4" />}
                                onClick={handleCalendarPlacement}
                                disabled={isBooking}
                            >
                                Termine im Kalender finden
                            </Button>
                        )}
                        <Button
                            rightSection={<ChevronRight className="size-4" />}
                            onClick={handleSeriesConfirm}
                            disabled={cannotBook}
                            loading={isBooking}
                        >
                            {isEditing ? 'Terminserie aktualisieren' : 'Terminserie buchen'}
                        </Button>
                    </div>
                </div>
                <PatientDuplicationPopup
                    visible={showDuplicationPopup}
                    setVisible={setShowDuplicationPopup}
                    setConfirm={handleConfirmDuplication}
                />
                <RescheduleNotificationModal
                    visible={showRescheduleModal}
                    setVisible={setShowRescheduleModal}
                    onConfirm={() => updateCollectionWithGuard(true)}
                    onCancel={() => updateCollectionWithGuard(false)}
                />
            </>
        );
    }

    return (
        <div className="flex w-full justify-end">
            <Button
                disabled={cannotProceed}
                loading={isBooking}
                rightSection={<ChevronRight className="size-4" />}
                onClick={handleSeriesNext}
            >
                Weiter
            </Button>
        </div>
    );
};
